import React from "react";
import {
  Box,
  Paper,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Grid2 as Grid,
  Typography,
  Switch,
  FormControlLabel,
  Chip,
  Container,
} from "@mui/material";
import { Save, Cancel, Delete } from "@mui/icons-material";
import { UseFormReturn, Controller } from "react-hook-form";
import {
  PromptTemplateDto,
  UpdatePromptTemplateDto,
} from "@/dto/PromptTemplateDto";

interface DetailFormProps {
  data: PromptTemplateDto | null;
  form: UseFormReturn<UpdatePromptTemplateDto>;
  loading: boolean;
  onSubmit: (data: UpdatePromptTemplateDto) => void;
  onDelete: () => void;
  onCancel: () => void;
}

const AI_PROVIDERS = [
  { value: "OPENAI", label: "OpenAI" },
  { value: "ANTHROPIC", label: "Anthropic" },
  { value: "GOOGLE", label: "Google" },
];

const AI_MODELS = {
  OPENAI: ["gpt-4o", "gpt-4o-mini", "gpt-4-turbo", "gpt-3.5-turbo"],
  ANTHROPIC: [
    "claude-3-5-sonnet-20241022",
    "claude-3-opus-20240229",
    "claude-3-haiku-20240307",
  ],
  GOOGLE: ["gemini-pro", "gemini-pro-vision"],
};

const CATEGORIES = [
  "boss_baby",
  "ai_manager",
  "customer_service",
  "content_generation",
];

export default function DetailForm({
  data,
  form,
  loading,
  onSubmit,
  onDelete,
  onCancel,
}: DetailFormProps) {
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = form;

  const selectedProvider = watch("aiModelProvider");
  const isActive = watch("isActive");

  if (!data) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography>Loading...</Typography>
      </Paper>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h6" gutterBottom>
            Template ID: {data.templateId}
          </Typography>
          <Chip
            label={data.isActive ? "Active" : "Inactive"}
            color={data.isActive ? "success" : "default"}
            size="small"
          />
        </Box>
        <FormControlLabel
          control={
            <Switch
              checked={isActive}
              onChange={(e) => setValue("isActive", e.target.checked)}
            />
          }
          label="Active"
        />
      </Box>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={3}>
          {/* Basic Information Section */}
          <Grid size={{ xs: 12 }}>
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
                Basic Information
              </Typography>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    {...register("templateKey", {
                      required: "Template key is required",
                      maxLength: {
                        value: 100,
                        message:
                          "Template key must be less than 100 characters",
                      },
                    })}
                    label="Template Key *"
                    fullWidth
                    error={!!errors.templateKey}
                    helperText={errors.templateKey?.message}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    {...register("templateName", {
                      required: "Template name is required",
                      maxLength: {
                        value: 200,
                        message:
                          "Template name must be less than 200 characters",
                      },
                    })}
                    label="Template Name *"
                    fullWidth
                    error={!!errors.templateName}
                    helperText={errors.templateName?.message}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <FormControl fullWidth error={!!errors.templateCategory}>
                    <InputLabel>Category *</InputLabel>
                    <Controller
                      name="templateCategory"
                      control={form.control}
                      rules={{ required: "Category is required" }}
                      render={({ field }) => (
                        <Select {...field} label="Category *">
                          {CATEGORIES.map((category) => (
                            <MenuItem key={category} value={category}>
                              {category}
                            </MenuItem>
                          ))}
                        </Select>
                      )}
                    />
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    {...register("priorityOrder")}
                    label="Priority Order"
                    type="number"
                    fullWidth
                    inputProps={{ min: 1 }}
                    helperText="Lower numbers have higher priority"
                  />
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <TextField
                    {...register("templateDescription")}
                    label="Description"
                    multiline
                    rows={3}
                    fullWidth
                    helperText="Brief description of this template's purpose"
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* AI Model Configuration Section */}
          <Grid size={{ xs: 12 }}>
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
                AI Model Configuration
              </Typography>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 4 }}>
                  <FormControl fullWidth>
                    <InputLabel>AI Provider</InputLabel>
                    <Controller
                      name="aiModelProvider"
                      control={form.control}
                      render={({ field }) => (
                        <Select {...field} label="AI Provider">
                          {AI_PROVIDERS.map((provider) => (
                            <MenuItem
                              key={provider.value}
                              value={provider.value}
                            >
                              {provider.label}
                            </MenuItem>
                          ))}
                        </Select>
                      )}
                    />
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                  <FormControl fullWidth>
                    <InputLabel>AI Model</InputLabel>
                    <Controller
                      name="aiModelName"
                      control={form.control}
                      render={({ field }) => (
                        <Select {...field} label="AI Model">
                          {AI_MODELS[
                            selectedProvider as keyof typeof AI_MODELS
                          ]?.map((model) => (
                            <MenuItem key={model} value={model}>
                              {model}
                            </MenuItem>
                          )) || []}
                        </Select>
                      )}
                    />
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12, md: 2 }}>
                  <TextField
                    {...register("aiModelTemperature", {
                      min: {
                        value: 0,
                        message: "Temperature must be between 0 and 2",
                      },
                      max: {
                        value: 2,
                        message: "Temperature must be between 0 and 2",
                      },
                    })}
                    label="Temperature"
                    type="number"
                    fullWidth
                    inputProps={{ min: 0, max: 2, step: 0.1 }}
                    error={!!errors.aiModelTemperature}
                    helperText={
                      errors.aiModelTemperature?.message || "0-2 range"
                    }
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 2 }}>
                  <TextField
                    {...register("aiMaxTokens")}
                    label="Max Tokens"
                    type="number"
                    fullWidth
                    inputProps={{ min: 1 }}
                    helperText="Maximum response length"
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Prompt Configuration Section */}
          <Grid size={{ xs: 12 }}>
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                Prompt Configuration
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Configure the prompts that will be sent to the AI model. Use
                placeholders like {"{"}variable{"}"} for dynamic content.
              </Typography>

              <Grid container spacing={3}>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    {...register("systemPrompt")}
                    label="System Prompt"
                    multiline
                    minRows={32}
                    maxRows={32}
                    fullWidth
                    placeholder="Enter the system prompt that defines the AI's role and behavior..."
                    helperText="This prompt sets the AI's role and context. It's sent before the user prompt."
                    sx={{
                      "& .MuiInputBase-input": {
                        fontFamily: "monospace",
                        fontSize: "0.9rem",
                        lineHeight: 1.6,
                      },
                      "& textarea": {
                        resize: "vertical",
                      },
                    }}
                  />
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <TextField
                    {...register("userPromptTemplate")}
                    label="User Prompt Template"
                    multiline
                    minRows={12}
                    maxRows={18}
                    fullWidth
                    placeholder="Enter the user prompt template with placeholders like {variable}..."
                    helperText="Template for user messages. Use {variable} syntax for dynamic content."
                    sx={{
                      "& .MuiInputBase-input": {
                        fontFamily: "monospace",
                        fontSize: "0.9rem",
                        lineHeight: 1.6,
                      },
                      "& textarea": {
                        resize: "vertical",
                      },
                    }}
                  />
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <TextField
                    {...register("assistantPrompt")}
                    label="Assistant Prompt"
                    multiline
                    minRows={10}
                    maxRows={15}
                    fullWidth
                    placeholder="Enter the assistant prompt for follow-up messages..."
                    helperText="Optional prompt for assistant's follow-up messages"
                    sx={{
                      "& .MuiInputBase-input": {
                        fontFamily: "monospace",
                        fontSize: "0.9rem",
                        lineHeight: 1.6,
                      },
                      "& textarea": {
                        resize: "vertical",
                      },
                    }}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    {...register("promptVariables")}
                    label="Prompt Variables (JSON)"
                    multiline
                    minRows={30}
                    maxRows={30}
                    fullWidth
                    placeholder='{"variable1": "default_value", "variable2": "another_value"}'
                    helperText="JSON object defining default values for prompt variables"
                    sx={{
                      "& .MuiInputBase-input": {
                        fontFamily: "monospace",
                        fontSize: "0.9rem",
                        lineHeight: 1.6,
                      },
                      "& textarea": {
                        resize: "vertical",
                      },
                    }}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    {...register("aiModelConfig")}
                    label="AI Model Config (JSON)"
                    multiline
                    minRows={30}
                    maxRows={30}
                    fullWidth
                    placeholder='{"max_tokens": 4000, "top_p": 0.9}'
                    helperText="Additional AI model configuration parameters in JSON format"
                    sx={{
                      "& .MuiInputBase-input": {
                        fontFamily: "monospace",
                        fontSize: "0.9rem",
                        lineHeight: 1.6,
                      },
                      "& textarea": {
                        resize: "vertical",
                      },
                    }}
                  />
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <TextField
                    {...register("useCondition")}
                    label="Use Condition"
                    multiline
                    rows={4}
                    fullWidth
                    placeholder="Enter conditions for when this template should be used..."
                    helperText="Conditions that determine when this template should be selected"
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Metadata and Actions */}
          <Grid size={{ xs: 12 }}>
            <Paper sx={{ p: 3 }}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  Created: {new Date(data.regDttm).toLocaleString()}
                  {data.udtDttm &&
                    ` | Updated: ${new Date(data.udtDttm).toLocaleString()}`}
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  justifyContent: "space-between",
                }}
              >
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<Delete />}
                  onClick={onDelete}
                  disabled={loading}
                >
                  Delete Template
                </Button>

                <Box sx={{ display: "flex", gap: 2 }}>
                  <Button
                    variant="outlined"
                    startIcon={<Cancel />}
                    onClick={onCancel}
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={<Save />}
                    disabled={loading}
                  >
                    {loading ? "Updating..." : "Update Template"}
                  </Button>
                </Box>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </form>
    </Container>
  );
}
